import { Api } from '@/api';
import type {
  WorkflowDefinitionEntity,
  WorkflowTriggerDefinitionKey,
} from '@/api/entity_types/workflow/WorkflowDefinition';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { toast } from 'vue-sonner';

export type UseWorkflowDefinitionsStoreReturn = {
  workflowDefinitions: WorkflowDefinitionEntity[];
  isLoaded: boolean;
};

export const useWorkflowDefinitionsStore = defineStore('workflowDefinitions', () => {
  const workflowDefinitions = ref<WorkflowDefinitionEntity[]>([]);
  const isLoaded = ref(false);

  const load = async () => {
    isLoaded.value = false;
    const response = await Api.WorkflowDefinitions.list();
    isLoaded.value = true;

    if (response.status === 'success') {
      workflowDefinitions.value = response.data;
    } else {
      const description = response.messages.join(', ') || 'Network Error';
      toast.error('Workflow Definitions Error', { description });
    }
  };

  load();

  const getDefinition = (triggerDefinitionKey: WorkflowTriggerDefinitionKey) => {
    const definition = workflowDefinitions.value.find(definition => definition.trigger.key === triggerDefinitionKey);

    if (!definition) {
      const availableDefinitions = workflowDefinitions.value.map(x => x.trigger.key).join(', ');
      throw new Error(
        `Definition key ${triggerDefinitionKey} does not exist in ${availableDefinitions}. Did this method call before the definitions were finished loading?`
      );
    }

    return definition;
  };

  return { workflowDefinitions, isLoaded, getDefinition };
});
